<script setup lang="ts">
import {onMounted, ref} from "vue";
import {
  ConnectionState,
  isSourceArray,
  type Message,
  RequestState,
  Sender,
  type Source
} from "@/types.js";
import {v7 as uuidv7} from "uuid";
import SourcesPanelCe from "@/components/Chat/SourcesPanel.vue";
import MessagePanelCe from "@/components/Chat/MessagePanel.vue";
import "../animations.css"

export type Feedback = {
  sessionId: string,
  messages: Message[],
  sources: string[],
  quality: {
    summary: number,
    sources: number,
  },
  comment: string
}

const props = defineProps<{
  websocketUrl: string,
  user: string,
  apiKey: string,
  initialSearch: string,
  scopes?: Record<string, boolean>
}>()

const emit = defineEmits<{
  searchResults: [searchResults: Record<string, any>[]],
  close: [],
  error: [error: Event]
}>()

const sessionId = uuidv7()

const connectionState = ref<ConnectionState>(ConnectionState.Connecting);
const requestState = ref<RequestState>(RequestState.Done);
let socket = ref<WebSocket | null>(null)
const messages = ref<Message[]>([])
const sources = ref(new Map<string, Source>())
const sourceOrder = ref<Source["uid"][]>([])
const showMore = ref<boolean>(false);
const showFeedbackModal = ref<boolean>(false);
const feedbackData = ref<Feedback>({
  sessionId,
  messages: [] as Message[],
  sources: [] as string[],
  quality: {
    summary: -1,
    sources: -1,
  },
  comment: ""
})


function addMessage(message: string, sender: Message["sender"]) {
  if (sender === Sender.Error) {
    messages.value.pop()
  }
  messages.value.push({sender: sender, text: message});
}

function addSources(new_sources: any) {
  if (!isSourceArray(new_sources)) {
    console.error("Invalid source array")
    return
  }
  for (const source of new_sources) {
    if (!sources.value.has(source.uid)) {
      sources.value.set(source.uid, source);
      sourceOrder.value.push(source.uid)
    }
  }
}

function submitFeedback(evt: Event) {
  evt.preventDefault()
  let fd = feedbackData.value;
  fd.sources = sourceOrder.value;
  fd.messages = [{
    text: props.initialSearch,
    sender: Sender.User
  }, ...messages.value]
  socket.value?.send(JSON.stringify({
    type: "feedback",
    content: fd
  }))
  showFeedbackModal.value = false
}

onMounted(() => {
  socket.value = new WebSocket(props.websocketUrl);
  socket.value.addEventListener("open", () => {
    socket.value?.send(props.apiKey);
    connectionState.value = ConnectionState.Connected
    sendMessage(props.initialSearch, false, true);
  });
  socket.value.addEventListener("close", () => {
    connectionState.value = ConnectionState.ConnectionLost
    emit('close')
  });
  socket.value.addEventListener("error", (e) => {
    connectionState.value = ConnectionState.ConnectionLost
    console.error(e);
    emit('error', e);
  });

  socket.value.addEventListener("message", (event) => {
    const message = JSON.parse(event.data);
    switch (message.type) {
      case "message":
        requestState.value = RequestState.Done;
        const lastMessage = messages.value[messages.value.length - 1];
        if (lastMessage) {
          lastMessage.text = message.content;
        } else {
          addMessage(message.content, Sender.AI);
        }
        break;
      case "sources":
        addSources(message.content)
        break;
      case "search_results":
        emit('searchResults', JSON.parse(message.content));
        break
      case "event":
        switch (message.content?.event) {
          case "message_stream":
            const lastMessage = messages.value[messages.value.length - 1];
            if (lastMessage) {
              lastMessage.text += message.content?.data;
            } else {
              addMessage(message.content?.data, Sender.AI);
            }
            break
          default:
            if (message.content?.event?.match(/^[a-z,A-Z]+_error$/)) {
              addMessage("Es ist ein Fehler aufgetreten. Versuchen Sie es bitte später erneut.", Sender.Error);
              console.error(message.content);
              requestState.value = RequestState.Done
            } else {
              console.debug(message);
            }
        }
        break;
      default:
        console.debug(message);
        break;
    }
  });
});

function sendMessage(message: string, addUserMessageToHistory = true, generateSearchResults = false) {
  requestState.value = RequestState.Searching;
  if (addUserMessageToHistory) {
    messages.value.push({sender: Sender.User, text: message});
  }
  const messageToSend = {
    message: message,
    user: props.user,
    scopes: props.scopes,
    search: generateSearchResults
  }
  socket.value?.send(JSON.stringify(messageToSend))

  messages.value.push({text: "", sender: Sender.AI})
}
</script>

<template>

  <h2>KI-Zusammenfassung</h2>
  <div class="chat-area">
    <div>
      <b-row class="chat-area-content">
        <b-col>
          <div style="display: block; position:relative;">

            <BCollapse v-model="showMore" id="chatCollapse">
              <message-panel-ce @sendMessage="sendMessage" :messages="messages" :source-order="sourceOrder" :sources="sources"
                                :session-id="sessionId" :request-state="requestState" :showMore="showMore"/>
            </BCollapse>
          </div>
        </b-col>
        <b-col id="source-column" cols="12" md="4">
          <sources-panel-ce :sources="sources" :source-order="sourceOrder" :show-more="showMore"/>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" md="8">
          <div class="more-button-container">
            <BButtonGroup>
              <b-button variant="outline-primary" class="more-button" @click="showMore = !showMore">
              <span v-if="!showMore">
               <i-bi-chevron-down></i-bi-chevron-down> Mehr anzeigen
              </span>
                <span v-else>
                <i-bi-chevron-up></i-bi-chevron-up> Weniger anzeigen
              </span>
              </b-button>
              <Transition name="scroll">
                <b-button v-if="showMore" variant="outline-primary" @click="showFeedbackModal=true">Feedback</b-button>
              </Transition>
            </BButtonGroup>
          </div>
        </b-col>
      </b-row>
      <Transition name="fade">
        <div class="cover" v-if="connectionState !== ConnectionState.Connected">
          <h4 v-if="connectionState === ConnectionState.Connecting">Verbindung wird hergestellt...</h4>
          <h4 v-if="connectionState === ConnectionState.ConnectionLost" style="color: red">Verbindung verloren!</h4>
        </div>
      </Transition>
    </div>
  </div>

  <BModal id="feedback-modal" title="Feedback" centered scrollable no-footer v-model="showFeedbackModal" size="lg">
    <b-form>
      <div class="">
        <label>
          Bewerten Sie die Qualität der Zusammenfassung:
        </label>
      </div>
      <BFormRadioGroup v-model="feedbackData.quality.summary" stacked required>
        <BFormRadio :value="3">Sehr hilfreich</BFormRadio>
        <BFormRadio :value="2">Hilfreich</BFormRadio>
        <BFormRadio :value="1">Korrekt, aber nicht hilfreich</BFormRadio>
        <BFormRadio :value="0">Inkorrekt</BFormRadio>
      </BFormRadioGroup>
      <div class="mt-2">
        <label>
          Bewerten Sie die Qualität der gefundenen Quellen (rechte Spalte):
        </label>
      </div>
      <BFormRadioGroup v-model="feedbackData.quality.sources" stacked required>
        <BFormRadio :value="3">Alle notwendigen Quellen sind vorhanden</BFormRadio>
        <BFormRadio :value="2">Alle Quellen passen zur Frage</BFormRadio>
        <BFormRadio :value="1">Einige Quellen passen zur Frage</BFormRadio>
        <BFormRadio :value="0">Keine Quelle passt zur Frage</BFormRadio>
      </BFormRadioGroup>
      <div class="mt-2">
        <label>
          Anmerkungen (optional):
        </label>
      </div>
      <BFormTextarea v-model="feedbackData.comment" placeholder="Ihre Anmerkungen..." rows="3"></BFormTextarea>

      <hr>
      <strong>Hinweis:</strong>
      Zu Analysezwecken wird Ihrem Feedback der Inhalt der aktuellen Unterhaltung (Frage, Antwort und Quellen)
      angehängt und gespeichert.
      <hr>

      <b-button type="submit" variant="primary" class="w-100 mt-2" @click="submitFeedback">
        Feedback senden
      </b-button>
    </b-form>
  </BModal>

  <hr>

</template>

